import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Download, GraduationCap, ListMusic, Loader2, Mail, Printer, Search, Send, Square, Star, Volume2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Revise } from "@/components/notes/Revise";

export default function Notes() {
  const { languages, lang } = useI18n();
  const [rows, setRows] = useState(null);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [ask, setAsk] = useState("");
  const [rooms, setRooms] = useState([]);
  const [passOn, setPassOn] = useState("");
  const [passLang, setPassLang] = useState(lang || "en");
  const [passRoom, setPassRoom] = useState("");
  const [passing, setPassing] = useState(false);
  const [sending, setSending] = useState(false);
  const [playing, setPlaying] = useState("");
  const [runAll, setRunAll] = useState(false);
  const [runAt, setRunAt] = useState(0);
  const runRef = useRef(null);
  const [revising, setRevising] = useState(false);
  const [practice, setPractice] = useState(null);

  const load = async () => {
    const { data } = await api.get("/messages/starred");
    setRows(data.rows);
    setFolders(data.folders || []);
  };

  useEffect(() => {
    load().catch(() => setRows([]));
    api.get("/rooms").then(({ data }) => setRooms(data)).catch(() => {});
    api.get("/revise/streak").then(({ data }) => setPractice(data)).catch(() => {});
  }, []);

  const shown = useMemo(() => {
    const term = ask.trim().toLowerCase();
    return (rows || []).filter((r) => (folder ? (r.folder || "") === folder : true))
      .filter((r) => (term
        ? `${r.text || ""} ${r.note || ""} ${r.folder || ""} ${r.room_name || ""}`
          .toLowerCase().includes(term)
        : true));
  }, [rows, folder, ask]);

  const save = async (row, patch) => {
    setRows((all) => all.map((r) => (r.message_id === row.message_id ? { ...r, ...patch } : r)));
    try {
      if ("folder" in patch) {
        await api.put(`/messages/starred/${row.message_id}/folder`, { folder: patch.folder });
      }
      if ("note" in patch) {
        await api.put(`/messages/starred/${row.message_id}/note`, { note: patch.note });
      }
      const { data } = await api.get("/messages/starred");
      setFolders(data.folders || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that");
    }
  };

  const unstar = async (row) => {
    setRows((all) => all.filter((r) => r.message_id !== row.message_id));
    try {
      await api.post(`/rooms/${row.room_id}/messages/${row.message_id}/star`, { on: false });
      toast.success("Star removed");
      load().catch(() => {});
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not remove that star");
    }
  };

  const passItOn = async (row) => {
    setPassing(true);
    try {
      const { data } = await api.post(`/messages/starred/${row.message_id}/send`,
                                      { lang: passLang, room_id: passRoom });
      if (data.sent_to) {
        toast.success(`Sent on to ${data.sent_to}`);
      } else {
        try {
          await navigator.clipboard.writeText(data.text);
          toast.success("Copied in their language — paste it anywhere");
        } catch {
          toast.success(data.text);
        }
      }
      setPassOn("");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send that on");
    } finally {
      setPassing(false);
    }
  };

  const sheetHtml = () => {
    const pots = shown.reduce((acc, r) => {
      const key = (r.folder || "").trim() || "Not filed";
      (acc[key] = acc[key] || []).push(r);
      return acc;
    }, {});
    const esc = (s) => String(s || "").replace(/[&<>]/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const blocks = Object.keys(pots).sort().map((folder) => `
      <h2>${esc(folder)} · ${pots[folder].length}</h2>
      <ul>${pots[folder].map((r) => `
        <li>
          <div class="where">${esc(r.room_name)}${r.sender_name ? ` · ${esc(r.sender_name)}` : ""}</div>
          <div class="said">${esc(r.text)}</div>
          ${r.note ? `<div class="why">${esc(r.note)}</div>` : ""}
        </li>`).join("")}</ul>`).join("");
    return `<!doctype html><html><head><meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>What you kept — ChatMiLu</title><style>
        body{font-family:system-ui,-apple-system,sans-serif;color:#12161F;max-width:680px;
             margin:0 auto;padding:28px 20px;line-height:1.5}
        h1{font-size:26px;margin:0}
        .sub{font-size:13px;color:#6B7280;margin:4px 0 0}
        h2{font-size:12px;letter-spacing:.14em;text-transform:uppercase;color:#1A7DFF;margin:26px 0 8px}
        ul{list-style:none;padding:0;margin:0}
        li{margin:0 0 14px;page-break-inside:avoid}
        .where{font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:#8B93A5}
        .said{font-size:15px}
        .why{font-size:13px;color:#1A7DFF}
        footer{font-size:12px;color:#8B93A5;margin-top:28px}
      </style></head><body>
      <h1>What you kept</h1>
      <p class="sub">${shown.length} message${shown.length === 1 ? "" : "s"} from ChatMiLu</p>
      ${blocks}<footer>chatmilu.com</footer></body></html>`;
  };

  const printSheet = () => {
    const sheet = window.open("", "_blank", "width=780,height=900");
    if (!sheet) {
      toast.error("Your browser blocked the print window");
      return;
    }
    sheet.document.write(sheetHtml());
    sheet.document.close();
    sheet.focus();
    sheet.print();
  };

  const downloadSheet = () => {
    const blob = new Blob([sheetHtml()], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `chatmilu-kept-${new Date().toISOString().slice(0, 10)}.html`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success("Saved — it opens in any phone browser");
  };

  const emailSheet = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/messages/starred/email");
      if (data.sent) {
        toast.success(`Sent to ${data.to} — ${data.kept} kept`);
      } else if (data.reason === "sendgrid_out_of_credit") {
        toast.error("Your email allowance has run out at SendGrid. Print the list for now.");
      } else {
        toast.error("The email did not go out. Try again in a little while.");
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send that list");
    } finally {
      setSending(false);
    }
  };

  const playKept = async (row) => {
    setPlaying(row.message_id);
    try {
      const res = await api.get(`/rooms/${row.room_id}/messages/${row.message_id}/speech`,
                                { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.onended = () => {
        setPlaying("");
        URL.revokeObjectURL(url);
      };
      await audio.play();
    } catch {
      toast.error("Could not read that one out");
      setPlaying("");
    }
  };

  const sayOne = (row) => new Promise((done, fail) => {
    api.get(`/rooms/${row.room_id}/messages/${row.message_id}/speech`, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        const audio = new Audio(url);
        runRef.current = audio;
        audio.onended = () => {
          URL.revokeObjectURL(url);
          done();
        };
        audio.onerror = () => fail(new Error("audio"));
        audio.play().catch(fail);
      })
      .catch(fail);
  });

  const playThemAll = async (from = 0) => {
    if (runAll) {
      runRef.current?.pause();
      runRef.current = null;
      setRunAll(false);
      return;
    }
    setRunAll(true);
    for (let i = from; i < shown.length; i += 1) {
      setRunAt(i);
      localStorage.setItem("cml_kept_playat", String(i));
      setPlaying(shown[i].message_id);
      try {
        await sayOne(shown[i]);
      } catch {
        toast.error("Could not read that one out");
        break;
      }
      if (!runRef.current) break;   // stopped mid-way
    }
    runRef.current = null;
    setPlaying("");
    setRunAll(false);
    localStorage.setItem("cml_kept_playat", "0");
  };

  if (rows === null) {
    return (
      <div className="mx-auto max-w-4xl px-5 py-16 text-center" data-testid="notes-loading">
        <Loader2 className="mx-auto h-5 w-5 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-5 py-8 sm:px-8" data-testid="notes-page">
      {revising && (
        <Revise rows={shown} onClose={() => setRevising(false)}
                onPractised={(run) => setPractice(run)} />
      )}
      <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
        What You Kept
      </h1>
      <p className="mt-2 max-w-2xl text-base text-ink-soft sm:text-lg">
        Every message you starred, the folder you filed it under and the line you wrote about why.
      </p>

      {practice && (practice.days > 0 || practice.best > 0) && (
        <p className="mt-3 inline-flex flex-wrap items-center gap-2 rounded-full border border-white/10 bg-panel px-3 py-1.5 text-xs"
           data-testid="revise-streak">
          <GraduationCap className={`h-3.5 w-3.5 ${practice.today ? "text-emerald-300" : "text-ink-soft"}`} />
          <span>
            <span className="font-semibold">{practice.days} day{practice.days === 1 ? "" : "s"}</span> of practice
            {practice.best > practice.days ? ` · best ${practice.best}` : ""}
          </span>
          {practice.badge ? (
            <span className="rounded-full border border-brand/40 px-2 py-0.5 text-[10px] font-semibold text-brand"
                  data-testid="revise-badge">{practice.badge}</span>
          ) : (
            <span className="text-ink-soft" data-testid="revise-to-badge">
              · {practice.to_badge} more for Daily Practice
            </span>
          )}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" />
          <input
            data-testid="notes-search"
            value={ask}
            onChange={(e) => setAsk(e.target.value)}
            placeholder="Search what you kept, your notes and folders"
            className="min-h-[42px] w-full rounded-full border border-white/10 bg-panel ps-10 pe-4 text-sm outline-none focus:border-brand/50"
          />
        </div>
        <datalist id="notes-folder-names">
          {["Phrases", "Plans", "Links", ...folders.map((f) => f.name)]
            .filter((n, i, all) => all.indexOf(n) === i)
            .map((n) => <option key={n} value={n} />)}
        </datalist>
        <button
          data-testid="notes-print"
          onClick={printSheet}
          disabled={shown.length === 0}
          className="inline-flex min-h-[42px] items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-semibold text-ink-soft transition-colors hover:border-brand/40 hover:text-ink disabled:opacity-40"
        >
          <Printer className="h-4 w-4" /> Print this list
        </button>
        <button
          data-testid="notes-download"
          onClick={downloadSheet}
          disabled={shown.length === 0}
          className="inline-flex min-h-[42px] items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-semibold text-ink-soft transition-colors hover:border-brand/40 hover:text-ink disabled:opacity-40"
        >
          <Download className="h-4 w-4" /> Download a copy
        </button>
        <button
          data-testid="notes-play-all"
          onClick={() => playThemAll(0)}
          disabled={shown.length === 0}
          className={`inline-flex min-h-[42px] items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-colors disabled:opacity-40 ${
            runAll ? "border-brand bg-brand/15 text-brand"
                   : "border-white/15 text-ink-soft hover:border-brand/40 hover:text-ink"}`}
        >
          {runAll ? <Square className="h-4 w-4" /> : <ListMusic className="h-4 w-4" />}
          {runAll ? `Stop · ${runAt + 1} of ${shown.length}` : "Play them all"}
        </button>
        <button
          data-testid="notes-revise"
          onClick={() => setRevising(true)}
          disabled={shown.length === 0}
          className="inline-flex min-h-[42px] items-center gap-2 rounded-full border border-brand/40 px-4 text-sm font-semibold text-brand transition-colors hover:bg-brand/10 disabled:opacity-40"
        >
          <GraduationCap className="h-4 w-4" /> Revise
        </button>
        {!runAll && Number(localStorage.getItem("cml_kept_playat") || 0) > 0 && (
          <button
            data-testid="notes-resume"
            onClick={() => playThemAll(Number(localStorage.getItem("cml_kept_playat")))}
            className="text-xs text-brand underline decoration-dotted"
          >
            Carry on where you stopped
          </button>
        )}
        <button
          data-testid="notes-email"
          onClick={emailSheet}
          disabled={sending || rows.length === 0}
          className="inline-flex min-h-[42px] items-center gap-2 rounded-full bg-brand px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
          {sending ? "Sending…" : "Email it to me"}
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="notes-folders">
        {[{ name: "", count: rows.length, label: "All" },
          ...folders.map((f) => ({ ...f, label: f.name })),
        ].map((f) => (
          <button
            key={f.name || "all"}
            data-testid={`notes-folder-${f.name || "all"}`}
            onClick={() => setFolder(f.name)}
            className={`min-h-[32px] rounded-full border px-3.5 text-xs font-semibold transition-colors ${
              folder === f.name
                ? "border-brand bg-brand/20 text-brand"
                : "border-white/15 text-ink-soft hover:border-brand/40 hover:text-ink"}`}
          >
            {f.label} {f.count}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="mt-10 text-sm text-ink-soft" data-testid="notes-empty">
          Nothing here yet. Tap the star on any message in{" "}
          <Link to="/messages" className="text-brand underline decoration-dotted">iMessages</Link>{" "}
          and it will be kept on this page.
        </p>
      ) : (
        <ul className="mt-6 space-y-3">
          {shown.map((row) => (
            <li key={row.message_id}
                data-testid={`note-card-${row.message_id}`}
                className="rounded-2xl border border-white/10 bg-panel p-4 transition-colors hover:border-brand/30">
              <div className="flex items-start justify-between gap-3">
                <p className="text-[11px] uppercase tracking-widest text-ink-soft">
                  {row.room_name}{row.sender_name ? ` · ${row.sender_name}` : ""}
                </p>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    data-testid={`note-listen-${row.message_id}`}
                    onClick={() => playKept(row)}
                    disabled={playing === row.message_id}
                    title="Hear it in your language"
                    className="inline-flex items-center gap-1 text-[11px] text-ink-soft transition-colors hover:text-brand disabled:opacity-60"
                  >
                    {playing === row.message_id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Volume2 className="h-3.5 w-3.5" />}
                    Listen
                  </button>
                  <Link
                    to={`/messages?room=${row.room_id}`}
                    data-testid={`note-open-${row.message_id}`}
                    title="Open the chat"
                    className="inline-flex items-center gap-1 text-[11px] text-ink-soft hover:text-brand"
                  >
                    Open chat <ArrowUpRight className="h-3 w-3" />
                  </Link>
                  <button
                    data-testid={`note-unstar-${row.message_id}`}
                    onClick={() => unstar(row)}
                    title="Remove the star"
                    className="text-amber-300 transition-colors hover:text-ink-soft"
                  >
                    <Star className="h-4 w-4 fill-current" />
                  </button>
                </div>
              </div>

              <p className="mt-2 text-sm" dir="auto">{row.text}</p>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input
                  data-testid={`note-folder-${row.message_id}`}
                  list="notes-folder-names"
                  defaultValue={row.folder || ""}
                  placeholder="Folder"
                  onBlur={(e) => {
                    const name = e.target.value.trim();
                    if (name !== (row.folder || "")) save(row, { folder: name });
                  }}
                  onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                  className="min-h-[32px] w-32 rounded-full border border-white/15 bg-page px-3 text-xs outline-none focus:border-brand/50"
                />
                <input
                  data-testid={`note-line-${row.message_id}`}
                  defaultValue={row.note || ""}
                  placeholder="Why I kept it"
                  onBlur={(e) => {
                    const note = e.target.value.trim();
                    if (note !== (row.note || "")) save(row, { note });
                  }}
                  onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                  className="min-h-[32px] min-w-[180px] flex-1 rounded-full border border-white/15 bg-page px-3 text-xs outline-none focus:border-brand/50"
                />
                {passOn === row.message_id ? (
                  <>
                    <select
                      data-testid={`note-pass-lang-${row.message_id}`}
                      value={passLang}
                      onChange={(e) => setPassLang(e.target.value)}
                      className="min-h-[32px] rounded-full border border-white/15 bg-page px-3 text-xs"
                    >
                      {languages.map((l) => (
                        <option key={l.code} value={l.code}>{l.name}</option>
                      ))}
                    </select>
                    <select
                      data-testid={`note-pass-room-${row.message_id}`}
                      value={passRoom}
                      onChange={(e) => setPassRoom(e.target.value)}
                      className="min-h-[32px] rounded-full border border-white/15 bg-page px-3 text-xs"
                    >
                      <option value="">Just give me the words</option>
                      {rooms.map((r) => (
                        <option key={r.room_id} value={r.room_id}>Send to {r.name}</option>
                      ))}
                    </select>
                    <button
                      data-testid={`note-pass-go-${row.message_id}`}
                      onClick={() => passItOn(row)}
                      disabled={passing}
                      className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full bg-brand px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-hover disabled:opacity-50"
                    >
                      {passing ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Send className="h-3.5 w-3.5" />} Go
                    </button>
                    <button data-testid={`note-pass-cancel-${row.message_id}`}
                            onClick={() => setPassOn("")}
                            className="text-xs text-ink-soft hover:text-ink">Cancel</button>
                  </>
                ) : (
                  <button
                    data-testid={`note-pass-open-${row.message_id}`}
                    onClick={() => {
                      setPassOn(row.message_id);
                      setPassRoom("");
                    }}
                    className="text-xs text-brand underline decoration-dotted"
                  >
                    Send it on in another language
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
